import { createContext, useState } from 'react'

import { useAxios } from '../../utilities/Requests/useAxios'
import { Provider } from '../ProvidersProvider/types'

export type ProviderTaskComment = {
  id: number
  providerTaskId: number
  createdByProviderId: number
  comment: string
  createdAt: string
  createdByProviderName: Provider
}

export interface ProviderTaskAssigneeResponse {
  providerId: number
  providerName: string
}

export interface ProviderTaskResponse {
  id: number
  patientId: number
  patientName: string

  assignedProviderType?: string
  assignedProviders: ProviderTaskAssigneeResponse[]

  providerTaskType: ProviderTaskType
  priorityLevel: ProviderTaskPriorityLevel
  status: ProviderTaskStatus
  barrier: ProviderTaskBarrier | null

  assignedAt: string
  dueAt: string
  createdAt: string

  providerTaskComments: ProviderTaskComment[]
}

export type AddCommentRequest = {
  providerTaskId: number
  comment: string
}

export type EditCommentRequest = {
  providerTaskCommentId: number
  comment: string
}

export type CreateProviderTaskRequest = {
  patientId: number
  providerTaskTypeId: number
  providerTypeId: number | null
  assignedToProviderId: number | null
  providerTaskPriorityLevelId: number
  providerTaskStatusId: number
  providerTaskBarrierId?: number | null
  assignedAt: string
  dueAt: string
}

export type UpdateProviderTaskRequest = {
  providerTaskId: number
  providerTaskTypeId: number | null
  providerTypeId: number | null
  assignedToProviderId: number | null
  providerTaskPriorityLevelId: number
  providerTaskStatusId: number
  providerTaskBarrierId?: number | null
  dueAt: string
}

export type ProviderTaskType = {
  id: number
  type: string
}

export type UpdateProviderTaskTypeRequest = {
  providerTaskTypeId: number
  newType: string
}

export type ProviderType = {
  id: number
  name: string
}

export type ProviderTaskPriorityLevel = {
  id: number
  level: string
}

export type ProviderTaskStatus = {
  id: number
  status: string
}

export type ProviderTaskBarrier = {
  id: number
  barrier: string
}

export type ProviderTaskHistory = {
  id: number
  fieldChanged: string
  oldValue: string
  newValue: string
  changedByProviderName: string
  changedAt: string
}

export interface ProviderTaskContextInterface {
  providerTasks: ProviderTaskResponse[] | null
  providerTaskHistory: ProviderTaskHistory[] | null
  getAllProviderTasks: () => Promise<any>
  getProviderTasksByProviderId: (providerId: number) => Promise<any>
  deleteProviderTask: (providerTaskId: number) => Promise<any>
  createProviderTaskType: (type: string) => Promise<any>
  updateProviderTaskType: (
    request: UpdateProviderTaskTypeRequest
  ) => Promise<any>
  getProviderTasksByPatientId: (patientId: number) => Promise<any>
  getProviderTaskById: (providerTaskId: number) => Promise<any>
  createProviderTask: (request: CreateProviderTaskRequest) => Promise<any>
  updateProviderTask: (request: UpdateProviderTaskRequest) => Promise<any>
  getProviderTaskTypes: () => Promise<any>
  getProviderTypes: () => Promise<any>
  getProviderTaskPriorityLevels: () => Promise<any>
  getProviderTaskStatuses: () => Promise<any>
  getProviderTaskBarriers: () => Promise<any>
  addProviderTaskComment: (request: AddCommentRequest) => Promise<any>
  editProviderTaskComment: (request: EditCommentRequest) => Promise<any>
  deleteProviderTaskComment: (providerTaskCommentId: number) => Promise<any>
  getProviderTaskHistory: (providerTaskId: number) => Promise<any>
}

export const ProviderTaskContext =
  createContext<ProviderTaskContextInterface | null>(null)

export const ProviderTaskProvider = ({ children }: any) => {
  const { fetch } = useAxios()

  const [providerTasks, setProviderTasks] = useState<
    ProviderTaskResponse[] | null
  >(null)

  const [providerTaskHistory, setProviderTaskHistory] = useState<
    ProviderTaskHistory[] | null
  >(null)

  const getAllProviderTasks = async () => {
    const { data, error } = await fetch({
      path: `ProviderTask/GetAllProviderTasks`,
    })

    if (data) {
      setProviderTasks([...data])
      return data
    } else {
      console.warn(`getAllProviderTasks-error: ${error}`)
    }
  }

  const getProviderTypes = async () => {
    const { data, error } = await fetch({
      path: `ProviderTask/GetProviderTypes`,
    })

    if (data) {
      return data
    } else {
      console.warn(`getAllProviderTypes-error: ${error}`)
    }
  }

  const getProviderTasksByProviderId = async (providerId: number) => {
    const { data, error } = await fetch({
      path: `ProviderTask/GetProviderTasksByProviderId?providerId=${providerId}`,
    })

    if (data) {
      setProviderTasks([...data])
      return data
    } else {
      console.warn(`getProviderTasksByProviderId-error: ${error}`)
    }
  }

  const deleteProviderTask = async (providerTaskId: number) => {
    const { data, error } = await fetch({
      path: `ProviderTask/DeleteProviderTask?providerTaskId=${providerTaskId}`,
      methodType: 'DELETE',
    })

    if (data) {
      return data
    } else {
      console.warn(`deleteProviderTask-error: ${error}`)
    }
  }

  const createProviderTaskType = async (type: string) => {
    const { data, error } = await fetch({
      path: `ProviderTask/CreateProviderTaskType?type=${type}`,
      methodType: 'POST',
    })

    if (data) {
      return data
    } else {
      console.warn(`createProviderTaskType-error: ${error}`)
    }
  }

  const updateProviderTaskType = async (
    request: UpdateProviderTaskTypeRequest
  ) => {
    const { data, error } = await fetch({
      path: `ProviderTask/UpdateProviderTaskType`,
      methodType: 'PUT',
      body: request,
    })

    if (data) {
      return data
    } else {
      console.warn(`UpdateProviderTaskType-error: ${error}`)
    }
  }

  const getProviderTasksByPatientId = async (patientId: number) => {
    const { data, error } = await fetch({
      path: `ProviderTask/GetProviderTasksByPatientId?patientId=${patientId}`,
    })

    if (data) {
      setProviderTasks([...data])
      return data
    } else {
      console.warn(`getProviderTasksByPatientId-error: ${error}`)
    }
  }

  const getProviderTaskById = async (providerTaskId: number) => {
    const { data, error } = await fetch({
      path: `ProviderTask/GetProviderTaskById?providerTaskId=${providerTaskId}`,
    })

    if (data) {
      return data
    } else {
      console.warn(`getProviderTaskById-error: ${error}`)
    }
  }

  const createProviderTask = async (request: CreateProviderTaskRequest) => {
    const { data, error } = await fetch({
      path: `ProviderTask/CreateProviderTask`,
      methodType: 'POST',
      body: request,
    })

    if (data) {
      return data
    } else {
      console.warn(`createProviderTask-error: ${error}`)
    }
  }

  const updateProviderTask = async (request: UpdateProviderTaskRequest) => {
    const { data, error } = await fetch({
      path: `ProviderTask/UpdateProviderTask`,
      methodType: 'PUT',
      body: request,
    })

    if (data) {
      return data
    } else {
      console.warn(`updateProviderTask-error: ${error}`)
    }
  }

  const getProviderTaskTypes = async () => {
    const { data, error } = await fetch({
      path: `ProviderTask/GetProviderTaskTypes`,
    })

    if (data) {
      return data
    } else {
      console.warn(`getProviderTaskTypes-error: ${error}`)
    }
  }

  const getProviderTaskPriorityLevels = async () => {
    const { data, error } = await fetch({
      path: `ProviderTask/GetProviderTaskPriorityLevels`,
    })

    if (data) {
      return data
    } else {
      console.warn(`getProviderTaskPriorityLevels-error: ${error}`)
    }
  }

  const getProviderTaskStatuses = async () => {
    const { data, error } = await fetch({
      path: `ProviderTask/GetProviderTaskStatuses`,
    })

    if (data) {
      return data
    } else {
      console.warn(`getProviderTaskStatuses-error: ${error}`)
    }
  }

  const getProviderTaskBarriers = async () => {
    const { data, error } = await fetch({
      path: `ProviderTask/GetProviderTaskBarriers`,
    })

    if (data) {
      return data
    } else {
      console.warn(`getProviderTaskBarriers-error: ${error}`)
    }
  }

  const addProviderTaskComment = async (request: AddCommentRequest) => {
    const { data, error } = await fetch({
      path: `ProviderTask/AddProviderTaskComment`,
      methodType: 'POST',
      body: request,
    })

    if (data) {
      return data
    } else {
      console.warn(`addProviderTaskComment-error: ${error}`)
    }
  }

  const editProviderTaskComment = async (request: EditCommentRequest) => {
    const { data, error } = await fetch({
      path: `ProviderTask/EditProviderTaskComment`,
      methodType: 'PUT',
      body: request,
    })

    if (data) {
      return data
    } else {
      console.warn(`editProviderTaskComment-error: ${error}`)
    }
  }

  const deleteProviderTaskComment = async (providerTaskCommentId: number) => {
    const { data, error } = await fetch({
      path: `ProviderTask/DeleteProviderTaskComment?providerTaskCommentId=${providerTaskCommentId}`,
      methodType: 'DELETE',
    })

    if (data) {
      return data
    } else {
      console.warn(`deleteProviderTaskComment-error: ${error}`)
    }
  }

  const getProviderTaskHistory = async (providerTaskId: number) => {
    const { data, error } = await fetch({
      path: `ProviderTask/GetProviderTaskHistory?providerTaskId=${providerTaskId}`,
    })

    if (data) {
      setProviderTaskHistory([...data])
      return data
    } else {
      console.warn(`getProviderTaskHistory-error: ${error}`)
    }
  }

  return (
    <ProviderTaskContext.Provider
      value={{
        providerTasks,
        providerTaskHistory,
        getProviderTasksByPatientId,
        getProviderTaskById,
        getAllProviderTasks,
        getProviderTasksByProviderId,
        createProviderTask,
        updateProviderTask,
        deleteProviderTask,
        getProviderTaskTypes,
        getProviderTypes,
        getProviderTaskPriorityLevels,
        getProviderTaskStatuses,
        getProviderTaskBarriers,
        createProviderTaskType,
        updateProviderTaskType,
        addProviderTaskComment,
        editProviderTaskComment,
        deleteProviderTaskComment,
        getProviderTaskHistory,
      }}
    >
      {children}
    </ProviderTaskContext.Provider>
  )
}
